import React, { createContext, useContext, useRef, useCallback } from "react";

interface RestoringViewContextProps {
  isRestoringView: React.MutableRefObject<boolean>;
  withRestoring: <T>(fn: () => T) => T;
}

const RestoringViewContext = createContext<RestoringViewContextProps | undefined>(undefined);

interface Props {
  children: React.ReactNode;
}

/**
 * Context that provides a flag to indicate when a saved view is being restored.
 * This allows ShipmentFiltersContext to skip auto-setting primaryFilter to ALL
 * when filters like selectedCustomer are set during view restoration.
 */
export const RestoringViewProvider = ({ children }: Props) => {
  const isRestoringView = useRef(false);

  // Helper to wrap a function call with the restoring flag
  const withRestoring = useCallback(<T,>(fn: () => T): T => {
    isRestoringView.current = true;
    const result = fn();
    // Use longer timeout to ensure React effects complete before clearing flag
    // React batches state updates and schedules effects after render cycle,
    // which can take longer than setTimeout(0)
    setTimeout(() => {
      isRestoringView.current = false;
    }, 100);
    return result;
  }, []);

  return (
    <RestoringViewContext.Provider value={{ isRestoringView, withRestoring }}>
      {children}
    </RestoringViewContext.Provider>
  );
};

export const useRestoringViewContext = () => {
  const context = useContext(RestoringViewContext);
  if (context === undefined) {
    throw new Error(
      "useRestoringViewContext must be used within a RestoringViewProvider"
    );
  }
  return context;
};
